import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as ArrowLeft } from '@elementary/common-ui/assets/arrow-left.svg';
import { ReactComponent as ArrowRight } from '@elementary/common-ui/assets/arrow-right-2.svg';
import {
  Breadcrumbs,
  Button,
  Flex,
  FlexColumn,
  IconButton,
  LoadingButton,
  OverflowTooltip,
  SvgIcon,
} from '@elementary/common-ui/components';
import {
  distinctArray,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { Typography, Dialog, Divider, Tooltip } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import useToggleTreeView from '@elementary/common-ui/hooks/useToggleTreeView';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useDataTestsEnabled from '@elementary/common-ui/hooks/useDataTestsEnabled';
import useHasDbtProjects from '@elementary/common-ui/hooks/useHasDbtProjects';
import {
  ANOMALY_TIME_WINDOW_DEFAULTS,
  AutomatedTestSeverities,
} from 'shared/constants';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';
import useEnvironments from 'shared/hooks/useEnvironments';
import useExitOnEnvChange from 'shared/hooks/useExitOnEnvChange';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/api';
import {
  AddTestForm,
  AddTestApi,
  CLOUD_TEST_TYPES,
  ColumnResource,
  AddTestApiItem,
  Resource,
  AddTestBaseApiItem,
  AddSingularTestApi,
  AddYamlTestApiItem,
  EditGenericTestApi,
  AddCloudTestApi,
  AddCustomSqlCloudTestApi,
  UpdateCustomSqlConfigApi,
  CheckDuplicatesResponse,
  DuplicateCloudTest,
  CheckCreatableTestsApi,
  CheckCreatableResponse,
  TestManagementGqlTestsResponse,
  TimestampFormField,
} from 'shared/types';
import {
  EditTestMetadata,
  PlanSource,
  WizardResourceType,
} from 'testManagement/types';
import {
  getConditionalSeverityValue,
  getStageValidationError,
  getTestArgs,
  getWizardStage,
  getWizardStages,
  isStageValid,
} from 'testManagement/utils/utils';
import { getSharedTimestampColumnNames } from 'testManagement/utils/wizardTimestampFields';
import { getArrayPropertyChanges } from 'catalog/components/BulkEditWizard/utils';
import useTestsPolling from 'shared/hooks/useTestsPolling';
import useTicketField from 'shared/hooks/useTicketField';
import {
  TEST_MANAGEMENT_TESTS,
  PULL_REQUESTS,
  TEST_CONFIGURATION,
  TEST_OVERVIEW,
} from '@elementary/common-ui/constants/queryKeys';
import useTestManagementAssetColumns from 'shared/hooks/useTestManagementAssetColumns';
import useTestManagementAssets from 'shared/hooks/useTestManagementAssets';
import useTestManagementTests from 'shared/hooks/useTestManagementTests';
import ConnectTicketDropdown from 'shared/components/ConnectTicketDropdown';
import { CreateTicketDialogs } from 'shared/components/CreateTicketDialogs';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';
import AddCustomQueryTestWizardStages from './AddCustomQueryTestWizardStages';
import AddTestWizardStages from './AddTestWizardStages';
import { CUSTOM_SQL_CLOUD_ERROR } from './constants';
import CustomQueryDestinationSelector from './CustomQueryDestinationSelector';
import {
  applyCustomSqlCloudSubmitErrors,
  buildCustomSqlConfig,
  buildCloudSqlTestSchedule,
  clearCustomQueryValidation,
  getCustomSqlCloudErrorCandidates,
  applyCustomSqlScheduleError,
  getCustomSqlCloudErrorCode,
  getCustomSqlValidateErrorMessage,
  isMutationError,
  seedMissingCustomSqlCloudFields,
} from './customSqlCloudTest';
import EditTestWizardStages from './EditTestWizardStages';
import GenerateCustomSqlPopupButton from './GenerateCustomSqlPopupButton';
import OverrideTestsConfirmationDialog from './OverrideTestsConfirmationDialog';
import PlanEditTestWizardStages from './PlanEditTestWizardStages';
import TestDialogStepper from './TestDialogStepper';
import { GeneratedCustomQuery } from './types';
import useWizardTestCatalog from './WizardTestStage/useWizardTestCatalog';
import useWizardUiContext from './useWizardUiContext';

const TIMESTAMP_FORM_FIELDS: TimestampFormField[] = [
  'timestampColumn',
  'updatedAtTimestampColumn',
];

const PageWrapper = styled(FlexColumn)`
  height: 100%;
  overflow: hidden;
`;

const PageHeader = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space('lg')};
  padding: ${space('xxl', 'lg', 'sm', 'lg')};
`;

const HeaderNavigation = styled(FlexColumn)`
  align-items: flex-start;
  gap: ${space('xs')};
  min-width: 0;
  flex: 1;
`;

const HeaderNavigationRow = styled(Flex)`
  align-items: center;
  gap: ${space('lg')};
  min-width: 0;
  width: 100%;
`;

const HeaderActions = styled(Flex)`
  align-items: center;
  gap: ${space('lg')};
  flex-shrink: 0;
`;

const SelectedCountLabel = styled.span`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.disabled};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding-left: ${space('huge')};
`;

const BackButton = styled(IconButton)`
  &:hover svg path {
    fill: ${({ theme }) => theme.palette.icon.primary} !important;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ButtonIconWrapper = styled(Flex)<{ $disabled: boolean }>`
  svg {
    path {
      fill: ${({ $disabled, theme }) =>
        $disabled ? theme.palette.icon.disabled : 'auto'};
      transition: fill 0.3s;
    }
  }
`;

const testPackageMap: Record<string, 'elementary' | 'dbt'> = {
  freshnessAnomalies: 'elementary',
  eventFreshnessAnomalies: 'elementary',
  volumeAnomalies: 'elementary',
  dimensionAnomalies: 'elementary',
  schemaChanges: 'elementary',
};

const testTypeMap: {
  [key in AddTestForm['testType']]: string;
} = {
  freshnessAnomalies: 'freshness_anomalies',
  cloudFreshnessAnomalies: 'freshness_anomalies',
  eventFreshnessAnomalies: 'event_freshness_anomalies',
  volumeAnomalies: 'volume_anomalies',
  cloudVolumeAnomalies: 'volume_anomalies',
  dimensionAnomalies: 'dimension_anomalies',
  schemaChanges: 'schema_changes',
  cloudDataContract: 'data_contract',
  cloudPipelineTaskPerformance: 'pipeline_task_performance',
  unique: 'unique',
  notNull: 'not_null',
  acceptedValues: 'accepted_values',
  relationship: 'relationships',
  yamlTest: '',
  customQuery: '',
  '': '',
};

type BaseProps = {
  wizardResourceType?: WizardResourceType;
  showStepper?: boolean;
};

export type AddWizardProps = BaseProps & {
  wizardMode: 'add';
};

export type EditWizardProps = BaseProps & {
  wizardMode: 'edit';
  initialValues: Partial<AddTestForm>;
  editTestMetadata: EditTestMetadata;
  editTestId?: string;
};

export type PlanWizardProps = BaseProps & {
  wizardMode: 'plan';
  planSource: PlanSource;
  planRowId: string;
  planEnvId?: string;
  initialValues: Partial<AddTestForm>;
  onPlanApply: (
    data: AddTestForm,
    dirtyFields: Record<string, unknown>,
  ) => void;
};

export type Props = AddWizardProps | EditWizardProps | PlanWizardProps;

const AddTestWizardPage = (props: Props) => {
  const {
    wizardMode,
    wizardResourceType = 'tables',
    showStepper = false,
  } = props;

  const isEditMode = wizardMode === 'edit';
  const isPlanMode = wizardMode === 'plan';
  const isConfigOnlyFlow = isEditMode || isPlanMode;
  const initialValues = isConfigOnlyFlow ? props.initialValues : undefined;
  const editTestMetadata = isEditMode ? props.editTestMetadata : undefined;
  const editTestId = isEditMode ? props.editTestId : undefined;

  const [stage, setStage] = useState(1);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [keepConfigStageMounted, setKeepConfigStageMounted] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateCloudTest[]>([]);
  const [pendingCloudTestArgs, setPendingCloudTestArgs] =
    useState<AddCloudTestApi | null>(null);
  const [notCreatableReason, setNotCreatableReason] = useState<string | null>(
    null,
  );

  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedEnv, selectedEnvId } = useEnvironments();
  const { data: account } = useAccount();
  const hasDbtProjects = useHasDbtProjects();
  const { shouldConnectCodeRepo } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });
  const navigate = useNavigateWithEnv();
  const navigateBack = useNavigate();
  const location = useLocation();
  const { setPollingTestsIds } = useTestsPolling();

  const { setTreeViewCollapsed } = useToggleTreeView({
    restoreOnUnmount: true,
  });

  useEffect(() => {
    setTreeViewCollapsed(true);
  }, [setTreeViewCollapsed]);

  useExitOnEnvChange('/test-management');

  const planSource = isPlanMode ? props.planSource : undefined;
  const planPreviewPath =
    planSource === 'code-plan'
      ? '/test-management/code-agent-preview'
      : planSource === 'custom-sql-plan'
      ? '/test-management/custom-sql-agent-preview'
      : '/test-management/cloud-agent-preview';

  const exitWizard = useCallback(() => {
    if (isPlanMode) {
      navigate(planPreviewPath);
      return;
    }
    if (location.key === 'default') {
      navigate('/test-management');
    } else {
      navigateBack(-1);
    }
  }, [location.key, navigate, navigateBack, isPlanMode, planPreviewPath]);

  const warehouseType = selectedEnv?.dwhConnection?.warehouseType;
  const isDataTestsEnabled = useDataTestsEnabled(
    isPlanMode ? props.planEnvId : undefined,
  );

  const methods = useForm<AddTestForm>({
    defaultValues: ANOMALY_TIME_WINDOW_DEFAULTS,
    shouldUnregister: true,
    mode: 'onChange',
  });

  const wizardStages = getWizardStages({
    wizardMode,
    wizardResourceType,
  });
  const currentWizardStage = getWizardStage(wizardStages, stage);

  const {
    ticketIdRef,
    requireTicketId,
    enabledIntegrations,
    activeIntegration,
    handleIntegrationSelect,
    createTicketDialogOpen,
    handleCreateTicketClick,
    handleCreateTicketClose,
    handleTicketCreated,
    resetTicket,
  } = useTicketField({
    envId: selectedEnvId,
    ticketId: methods.watch('ticketId') ?? null,
    setTicketId: (id) =>
      methods.setValue('ticketId', id, {
        shouldDirty: true,
        shouldValidate: true,
      }),
  });

  // This logic is to avoid changing the url by multiple logics simultaneously
  const navigateWithDelay = useCallback(
    (url: string) => {
      setTimeout(() => {
        navigate(url);
      }, 50);
    },
    [navigate],
  );

  const { data: resourcesData, isLoading: isResourcesLoading } =
    useTestManagementAssets();
  const {
    data: columnsByAssetId,
    isLoading: isColumnsLoading,
    isError: isColumnsError,
  } = useTestManagementAssetColumns({
    enabled: resourcesData !== undefined,
    onError: () =>
      openSnackbar({
        message: 'Failed to load columns. Please refresh and try again.',
        variant: 'error',
      }),
  });

  const { data: singularTestPaths } = useTestManagementTests<string[]>({
    select: (data: TestManagementGqlTestsResponse) => {
      const paths = (data?.tests ?? [])
        .filter((test) => test.isSingular)
        .map((t) => {
          const segments = (t.path ?? '').split('/');
          segments.pop();
          return segments.join('/');
        });
      return distinctArray(paths);
    },
  });

  const resetWizardState = () => {
    setStage(1);
    methods.setValue('resourceType', 'tables');
    methods.setValue('testType', '');
    setNotCreatableReason(null);
    resetTicket();
  };

  const onDbtTestSubmitSuccess = () => {
    resetWizardState();
    navigateWithDelay('/pull-requests');
  };

  const onEditSubmitSuccess = () => {
    resetWizardState();
    openSnackbar({
      message:
        'Creating PR for your suggested changes... This may take a few seconds',
    });
    exitWizard();
  };

  const { mutateAsync: addTests, isLoading: isAddTestsLoading } = useMutation(
    (value: AddTestApi) =>
      api.addTests(value, selectedEnvId!, ticketIdRef.current || undefined),
    {
      onSuccess: onDbtTestSubmitSuccess,
    },
  );

  const { mutateAsync: addSingularTest, isLoading: isAddSingularTestLoading } =
    useMutation(
      (value: AddSingularTestApi) =>
        api.addSingularTest(
          value,
          selectedEnvId!,
          ticketIdRef.current || undefined,
        ),
      {
        onSuccess: onDbtTestSubmitSuccess,
      },
    );

  const { mutateAsync: addYamlTests, isLoading: isAddYamlTestLoading } =
    useMutation(
      (value: AddTestApi) =>
        api.addYamlTests(
          value,
          selectedEnvId!,
          ticketIdRef.current || undefined,
        ),
      {
        onSuccess: onDbtTestSubmitSuccess,
      },
    );

  const { mutateAsync: checkCreatable, isLoading: isCheckCreatableLoading } =
    useMutation((value: CheckCreatableTestsApi) =>
      api.checkCreatableTests(value, selectedEnvId!),
    );

  const { mutateAsync: checkDuplicates, isLoading: isCheckDuplicatesLoading } =
    useMutation(
      (value: AddCloudTestApi) =>
        api.checkDuplicateCloudTests(value, selectedEnvId!),
      {
        onSuccess: (
          res: CheckDuplicatesResponse,
          variables: AddCloudTestApi,
        ) => {
          if (res.duplicates.length > 0) {
            setDuplicates(res.duplicates);
            setPendingCloudTestArgs(variables);
            setShowOverrideDialog(true);
          } else {
            addCloudTest({ value: variables, override: false });
          }
        },
      },
    );

  const { mutateAsync: addCloudTest, isLoading: isAddCloudTestLoading } =
    useMutation(
      ({ value, override }: { value: AddCloudTestApi; override: boolean }) =>
        api.addCloudTest(value, selectedEnvId!, override),
      {
        onSuccess: (res) => {
          openSnackbar({
            message: `Cloud ${res?.length > 1 ? 'tests' : 'test'} ${
              duplicates.length > 0 ? 'updated' : 'added'
            } successfully! Changes will be applied shortly.`,
            variant: 'success',
          });

          queryClient.invalidateQueries({
            queryKey: [TEST_MANAGEMENT_TESTS, selectedEnvId],
          });

          setPollingTestsIds((prev) => {
            const newSet = new Set(prev);
            res.forEach((r: { test_id: string }) => newSet.add(r.test_id));
            return newSet;
          });

          setDuplicates([]);
          setPendingCloudTestArgs(null);
          resetWizardState();
          navigateWithDelay('/test-management');
        },
      },
    );

  const {
    mutateAsync: addCustomSqlCloudTest,
    isLoading: isAddCustomSqlCloudTestLoading,
  } = useMutation(
    (value: AddCustomSqlCloudTestApi) =>
      api.addCustomSqlCloudTest(value, selectedEnvId!),
    {
      onSuccess: (res) => {
        openSnackbar({
          message:
            'Cloud test added successfully! Changes will be applied shortly.',
          variant: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: [TEST_MANAGEMENT_TESTS, selectedEnvId],
        });
        setPollingTestsIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(res.test_id);
          return newSet;
        });
        resetWizardState();
        navigateWithDelay('/test-management');
      },
    },
  );

  const handleOverrideConfirm = async () => {
    setShowOverrideDialog(false);
    if (pendingCloudTestArgs) {
      await addCloudTest({ value: pendingCloudTestArgs, override: true });
    }
  };

  const handleOverrideCancel = () => {
    setShowOverrideDialog(false);
    setDuplicates([]);
    setPendingCloudTestArgs(null);
  };

  const { mutateAsync: editGenericTest, isLoading: isEditGenericTestLoading } =
    useMutation(
      (args: { data: { edit_tests: EditGenericTestApi[] } }) =>
        api.editGenericTest(
          args,
          selectedEnvId!,
          ticketIdRef.current || undefined,
        ),
      {
        onSuccess: onEditSubmitSuccess,
      },
    );

  const {
    mutateAsync: updateCustomSqlConfig,
    isLoading: isUpdateCustomSqlConfigLoading,
  } = useMutation((value: UpdateCustomSqlConfigApi) =>
    api.updateCustomSqlConfig(selectedEnvId!, editTestId!, value),
  );

  const tableResourcesData = useMemo(
    () =>
      (resourcesData ?? []).map((item) => ({
        ...item,
        columns: columnsByAssetId?.[item.id] ?? [],
      })),
    [resourcesData, columnsByAssetId],
  );

  const columnsResourcesData: ColumnResource[] = useMemo(
    () =>
      tableResourcesData
        .filter((item) => item.source_type !== 'python_sdk')
        .map((item) =>
          item.columns.map((col) => ({
            id: `${item.id}.${col.name}`,
            asset_id: item.id,
            schema: item.schema,
            model_id: item.id,
            model_name: item.name,
            source_name: item.source_name,
            owners: item.owners,
            tags: item.tags,
            table_type: item.table_type,
            source_type: item.source_type,
            table_fqn: item.fqn,
            db_name: item.db_name,
            table_name: item.table_name || item.name,
            ...col,
          })),
        )
        .flat(),
    [tableResourcesData],
  );

  const targetResourceType = methods.watch('resourceType');
  const selectableResourceIds = useMemo(() => {
    if (wizardResourceType !== 'tables' && wizardResourceType !== 'columns') {
      return undefined;
    }
    const rows =
      targetResourceType === 'columns'
        ? columnsResourcesData
        : tableResourcesData;
    return rows.map((row) => row.id);
  }, [
    wizardResourceType,
    targetResourceType,
    columnsResourcesData,
    tableResourcesData,
  ]);

  const { selectableTests } = useWizardTestCatalog({
    resourceType: methods.watch('resourceType'),
    selectedResourceIds: methods.watch('resources') ?? [],
    tableResourcesData,
    columnsResourcesData,
    warehouseType,
    enabled:
      wizardResourceType === 'tables' || wizardResourceType === 'columns',
  });

  const getCommonSubmitArgs = (data: AddTestForm): AddTestBaseApiItem => ({
    name:
      data.testType === 'customQuery'
        ? data.testName
        : testTypeMap[data.testType],
    owners: data.owners,
    description: data.description !== '' ? data.description : undefined,
    tags: data.tags,
    severity: data.severity?.type,
    quality_dimension: data.qualityDimension,
  });

  const { dirtyFields } = methods.formState;

  const isValueEdited = (
    key: keyof AddTestForm,
    value?: AddTestForm[keyof AddTestForm],
  ) =>
    JSON.stringify(initialValues?.[key] || {}) !== JSON.stringify(value || {});

  const isTestConfigEdited =
    !!initialValues &&
    Object.keys(initialValues).some((key) =>
      isValueEdited(
        key as keyof AddTestForm,
        methods.watch(key as keyof AddTestForm),
      ),
    );

  const validateCreatable = async (
    args: CheckCreatableTestsApi,
  ): Promise<boolean> => {
    const creatableRes: CheckCreatableResponse = await checkCreatable(args);
    const deniedReasons = creatableRes.results
      .filter((r) => !r.creatable)
      .map((r) => r.reason)
      .filter((reason): reason is string => !!reason);
    if (deniedReasons.length > 0) {
      setNotCreatableReason([...new Set(deniedReasons)].join(' '));
      return false;
    }
    setNotCreatableReason(null);
    return true;
  };

  const onSubmit = async (data: AddTestForm) => {
    if (isPlanMode) {
      if (!props.planRowId) {
        exitWizard();
        return;
      }
      props.onPlanApply(
        data,
        methods.formState.dirtyFields as Record<string, unknown>,
      );
      navigate(planPreviewPath);
      return;
    }

    if (data.testType === 'yamlTest') {
      injectNameIntoYaml();
    }

    const submitData: AddTestForm =
      data.testType === 'yamlTest'
        ? { ...data, yaml: methods.getValues('yaml') }
        : data;

    const resourceType = methods.watch('resourceType');
    const isCustomQuery = methods.watch('testType') === 'customQuery';
    const isCustomSqlCloud =
      isCustomQuery && submitData.customQueryDestination === 'cloud';
    const isYamlTest = methods.watch('testType') === 'yamlTest';
    const isCloudTest = (CLOUD_TEST_TYPES as readonly string[]).includes(
      methods.watch('testType'),
    );

    const sourceName = tableResourcesData.find(
      (r) => r.id === methods.watch('resources')?.[0],
    )?.source_name;

    if (resourceType === 'columns' && columnsByAssetId === undefined) {
      openSnackbar({
        message: isColumnsError
          ? 'Failed to load columns. Please refresh and try again.'
          : 'Columns are still loading, please try again in a moment.',
        variant: 'error',
      });
      return;
    }

    if (isEditMode && isCustomSqlCloud) {
      if (!editTestId) return;

      const payload: UpdateCustomSqlConfigApi = {
        name: submitData.testName,
        description:
          submitData.description !== '' ? submitData.description : undefined,
        sql: submitData.customQuery,
        schedule: buildCloudSqlTestSchedule(submitData),
        primary_asset_id: submitData.customSqlPrimaryAssetId ?? null,
        severity:
          submitData.severity?.type === 'error'
            ? AutomatedTestSeverities.ERROR
            : AutomatedTestSeverities.WARN,
        config: buildCustomSqlConfig(submitData),
      };

      try {
        await updateCustomSqlConfig(payload);
        const ownersChanged = isValueEdited('owners', submitData.owners);
        const tagsChanged = isValueEdited('tags', submitData.tags);
        if (ownersChanged || tagsChanged) {
          await api.updateTest(selectedEnvId!, editTestId, {
            ...(ownersChanged ? { owners: submitData.owners } : {}),
            ...(tagsChanged ? { tags: submitData.tags } : {}),
          });
        }

        openSnackbar({
          message:
            'Cloud test updated successfully! Changes will be applied shortly.',
          variant: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: [TEST_MANAGEMENT_TESTS, selectedEnvId],
        });
        queryClient.invalidateQueries({ queryKey: [TEST_CONFIGURATION] });
        queryClient.invalidateQueries({ queryKey: [TEST_OVERVIEW] });
        setPollingTestsIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(editTestId);
          return newSet;
        });
        resetWizardState();
        exitWizard();
      } catch (error) {
        if (!isMutationError(error)) {
          setNotCreatableReason('Error');
          return;
        }
        if (applyCustomSqlScheduleError(methods, error)) return;
        const code = getCustomSqlCloudErrorCode(error);
        if (code === CUSTOM_SQL_CLOUD_ERROR.ASSET_SELECTION_REQUIRED) {
          methods.setValue('customSqlValidated', true);
          methods.setValue('customSqlRequiresAssetSelection', true);
          methods.setValue(
            'customSqlReferencedAssets',
            getCustomSqlCloudErrorCandidates(error),
          );
          methods.setValue('customSqlPrimaryAssetId', null);
          setNotCreatableReason('Select which table this test belongs to');
          return;
        }
        setNotCreatableReason(getCustomSqlValidateErrorMessage(error));
      }
      return;
    }

    if (isEditMode) {
      const originalOwners = initialValues?.owners || [];
      const originalTags = initialValues?.tags || [];
      const currentOwners = submitData.owners || [];
      const currentTags = submitData.tags || [];
      const ownersChanges = isValueEdited('owners', submitData.owners)
        ? getArrayPropertyChanges(originalOwners, currentOwners)
        : undefined;
      const tagsChanges = isValueEdited('tags', submitData.tags)
        ? getArrayPropertyChanges(originalTags, currentTags)
        : undefined;

      const editArgs: EditGenericTestApi[] = submitData.resources.map(
        (resource) => {
          const resourceDataItem =
            methods.watch('resourceType') === 'columns'
              ? columnsResourcesData.find((res) => res.id === resource)
              : tableResourcesData.find((res) => res.id === resource);
          const assetId =
            methods.watch('resourceType') === 'columns' && resourceDataItem
              ? (resourceDataItem as ColumnResource).asset_id
              : resource;

          return {
            owners: ownersChanges,
            tags: tagsChanges,
            description: isValueEdited('description', submitData.description)
              ? submitData.description
              : undefined,
            severity: isValueEdited('severity', submitData.severity)
              ? submitData.severity?.type
              : undefined,
            ...(isValueEdited('severity', submitData.severity)
              ? getConditionalSeverityValue(methods.watch)
              : {}),
            name: editTestMetadata?.name || '',
            // FE tableName (tableAsset) maps to BE resource_name
            resource_name: editTestMetadata?.tableName || '',
            source_name: sourceName,
            package: editTestMetadata?.package,
            asset_id: assetId,
            column:
              methods.watch('resourceType') === 'columns'
                ? editTestMetadata?.resourceName
                    ?.split('.')
                    .pop()
                    ?.toLowerCase()
                : undefined,
          };
        },
      );

      await editGenericTest({ data: { edit_tests: editArgs } });
    } else if (isCustomSqlCloud) {
      const payload: AddCustomSqlCloudTestApi = {
        name: submitData.testName,
        description:
          submitData.description !== '' ? submitData.description : undefined,
        sql: submitData.customQuery,
        schedule: buildCloudSqlTestSchedule(submitData),
        primary_asset_id: submitData.customSqlPrimaryAssetId ?? null,
        severity:
          submitData.severity?.type === 'error'
            ? AutomatedTestSeverities.ERROR
            : AutomatedTestSeverities.WARN,
        owners: submitData.owners,
        tags: submitData.tags,
        config: buildCustomSqlConfig(submitData),
      };

      try {
        await addCustomSqlCloudTest(payload);
      } catch (error) {
        if (!isMutationError(error)) {
          setNotCreatableReason('Error');
          return;
        }
        if (applyCustomSqlScheduleError(methods, error)) return;
        const code = getCustomSqlCloudErrorCode(error);
        if (code === CUSTOM_SQL_CLOUD_ERROR.ASSET_SELECTION_REQUIRED) {
          methods.setValue('customSqlValidated', true);
          methods.setValue('customSqlRequiresAssetSelection', true);
          methods.setValue(
            'customSqlReferencedAssets',
            getCustomSqlCloudErrorCandidates(error),
          );
          methods.setValue('customSqlPrimaryAssetId', null);
          setNotCreatableReason('Select which table this test belongs to');
          return;
        }
        setNotCreatableReason(getCustomSqlValidateErrorMessage(error));
      }
      return;
    } else if (isCustomQuery) {
      const isCreatable = await validateCreatable([
        {
          asset_id: data.testedTable || undefined,
          name: data.testName || '',
          tags: data.tags,
          owners: data.owners,
        },
      ]);
      if (!isCreatable) return;

      const arg: AddSingularTestApi = {
        ...getCommonSubmitArgs(submitData),
        path: submitData.path,
        sql_query: submitData.customQueryConverted,
        override_primary_test_model_id: submitData.testedTable || undefined,
      };

      await addSingularTest(arg);
    } else if (isCloudTest) {
      const resourceData: Resource[] = tableResourcesData; // For the future we will support column cloud tests
      setNotCreatableReason(null);

      const isDataContractCloudTest =
        submitData.testType === 'cloudDataContract';

      const addCloudTestArgs: AddCloudTestApi = submitData.resources.map(
        (resource) => {
          const dataItem = resourceData.find((res) => res.id === resource);

          const baseArgs = {
            test_type: testTypeMap[submitData.testType] || '',
            database_name: dataItem?.db_name || null,
            schema_name: dataItem?.schema || '',
            table_name: dataItem?.table_name || dataItem?.name || '',
            model_unique_id: dataItem?.id || '',
            owners: submitData.owners,
            tags: submitData.tags,
            severity:
              submitData.severity?.type === 'error'
                ? AutomatedTestSeverities.ERROR
                : AutomatedTestSeverities.WARN,
          };

          if (isDataContractCloudTest) {
            return {
              ...baseArgs,
              baseline_mode: submitData.baselineMode || 'complete',
              ...(submitData.baselineMode === 'manual'
                ? { baseline_columns: submitData.baselineColumns }
                : {}),
              enforcement_level: submitData.enforcementLevel || 'all',
            };
          }

          const {
            sensitivity,
            anomalyDirection,
            detectionPeriod,
            fixedThreshold,
            trainingPeriod,
            spikeThreshold,
            dropThreshold,
            failureThresholdGrace,
          } = submitData?.anomalyConfig || {};

          if (submitData.testType === 'cloudPipelineTaskPerformance') {
            const isPipelineThreshold = fixedThreshold != null;
            return {
              ...baseArgs,
              config: isPipelineThreshold
                ? {
                    mode: 'threshold',
                    fixed_threshold: fixedThreshold,
                  }
                : {
                    mode: 'anomaly',
                    ...(sensitivity ? { sensitivity } : {}),
                    ...(anomalyDirection
                      ? { anomaly_direction: anomalyDirection }
                      : {}),
                    ...(trainingPeriod
                      ? { training_period: trainingPeriod }
                      : {}),
                  },
            };
          }

          const isVolumeThresholdMode =
            submitData.testType === 'cloudVolumeAnomalies' &&
            submitData.volumeDetectionMode === 'percentage_thresholds';

          const includeSpike =
            isVolumeThresholdMode &&
            spikeThreshold &&
            anomalyDirection !== 'DROP';
          const includeDrop =
            isVolumeThresholdMode &&
            dropThreshold &&
            anomalyDirection !== 'SPIKE';

          return {
            ...baseArgs,
            anomaly_config: {
              // In threshold mode the runner ignores sensitivity, so don't send it.
              ...(sensitivity && !isVolumeThresholdMode ? { sensitivity } : {}),
              ...(anomalyDirection
                ? { anomaly_direction: anomalyDirection }
                : {}),
              ...(detectionPeriod ? { detection_period: detectionPeriod } : {}),
              ...(fixedThreshold ? { fixed_threshold: fixedThreshold } : {}),
              ...(trainingPeriod ? { training_period: trainingPeriod } : {}),
              ...(includeSpike ? { spike_threshold: spikeThreshold } : {}),
              ...(includeDrop ? { drop_threshold: dropThreshold } : {}),
              ...(failureThresholdGrace
                ? { failure_threshold_grace: failureThresholdGrace }
                : {}),
            },
          };
        },
      );

      const checkCreatableArgs: CheckCreatableTestsApi = data.resources.map(
        (resource) => {
          const dataItem = resourceData.find((res) => res.id === resource);
          return {
            asset_id: dataItem?.id,
            database_name: dataItem?.db_name || '',
            schema_name: dataItem?.schema || '',
            table_name: dataItem?.table_name || dataItem?.name || '',
            name: testTypeMap[data.testType] || '',
            tags: data.tags,
            owners: data.owners,
          };
        },
      );
      const isCreatable = await validateCreatable(checkCreatableArgs);
      if (!isCreatable) return;

      await checkDuplicates(addCloudTestArgs);
    } else {
      const resourceData: (Resource | ColumnResource)[] =
        resourceType === 'tables' ? tableResourcesData : columnsResourcesData;

      const addTestArg: Array<AddTestApiItem | AddYamlTestApiItem> =
        submitData.resources.map((resource) => {
          const dataItem = resourceData.find((res) => res.id === resource);

          const resourceName =
            resourceType === 'tables'
              ? dataItem?.name
              : (dataItem as ColumnResource)?.model_name;

          const argBase = {
            ...getCommonSubmitArgs(submitData),
            resource_name: resourceName || '',
            source_name: resourceData.find((res) => res.id === resource)
              ?.source_name,
          };

          let arg: AddYamlTestApiItem | AddTestApiItem;

          const assetId =
            resourceType === 'columns' && dataItem
              ? (dataItem as ColumnResource).asset_id
              : resource;

          if (isYamlTest) {
            arg = {
              ...argBase,
              ...getConditionalSeverityValue(methods.watch),
              yaml: submitData.yaml,
              name: 'yamlTest',
              asset_id: assetId,
              where: submitData.whereClause || undefined,
              limit: submitData.limit || undefined,
              store_failures: submitData.shouldStoreFailures,
            };
          } else {
            arg = {
              ...argBase,
              package: testPackageMap[submitData.testType],
              ...getConditionalSeverityValue(methods.watch),
              args: getTestArgs(submitData),
              asset_id: assetId,
            };
          }

          if (resourceType === 'columns') {
            arg.column = resourceData.find((res) => res.id === resource)?.name;
          }
          return arg;
        });

      const checkCreatableArgs: CheckCreatableTestsApi = addTestArg.map(
        (testArg) => {
          const yamlTestName =
            isYamlTest && 'yaml' in testArg
              ? testArg.yaml
                  .match(/^\s*name:\s*['"]?([^'"\n#]+)['"]?\s*$/m)?.[1]
                  ?.trim()
              : undefined;
          const effectiveName =
            isYamlTest && (yamlTestName || data.testNameOverride?.trim())
              ? yamlTestName || data.testNameOverride?.trim() || ''
              : testArg.name || '';
          return {
            asset_id: testArg.asset_id,
            name: effectiveName,
            tags: testArg.tags,
            owners: testArg.owners,
          };
        },
      );
      const isCreatable = await validateCreatable(checkCreatableArgs);
      if (!isCreatable) return;

      const submissionMethod = isYamlTest ? addYamlTests : addTests;
      await submissionMethod(addTestArg);
    }

    queryClient.invalidateQueries([PULL_REQUESTS]);
  };

  const handleClose = () => {
    setShowCloseConfirmDialog(false);
    setStage(1);
    setNotCreatableReason(null);
    resetTicket();

    methods.setValue('resourceType', 'tables');
    methods.setValue('testType', '');
    methods.setValue('yamlTemplateName', '');

    exitWizard();
  };

  const requestExit = () => {
    const hasUnsavedChanges = isPlanMode
      ? Object.keys(dirtyFields).length > 0
      : isEditMode
      ? isTestConfigEdited
      : !!methods.watch('resources')?.length || !!methods.watch('customQuery');

    if (hasUnsavedChanges) {
      setShowCloseConfirmDialog(true);
    } else {
      handleClose();
    }
  };

  const testTypeWatch = methods.watch('testType');
  const isCloudCustomSql =
    testTypeWatch === 'customQuery' &&
    methods.watch('customQueryDestination') === 'cloud';
  const isCustomQueryPrBlocked =
    testTypeWatch === 'customQuery' &&
    !isCloudCustomSql &&
    shouldConnectCodeRepo;

  useEffect(() => {
    setKeepConfigStageMounted(false);
  }, [testTypeWatch]);

  const handleCustomQueryGenerated = ({
    sql,
    description,
    qualityDimension,
  }: GeneratedCustomQuery) => {
    methods.setValue('customQuery', sql, { shouldDirty: true });
    clearCustomQueryValidation(methods);
    methods.clearErrors('customQuery');
    if (description && !methods.getValues('description')?.trim()) {
      methods.setValue('description', description, { shouldDirty: true });
    }
    if (qualityDimension && !methods.getValues('qualityDimension')) {
      methods.setValue('qualityDimension', qualityDimension, {
        shouldDirty: true,
      });
    }
  };

  useEffect(() => {
    const seedInitialValues = () => {
      Object.entries(initialValues || {}).forEach(([key, value]) => {
        methods.setValue(key as keyof AddTestForm, value);
      });
    };

    if (isPlanMode) {
      seedInitialValues();
      // Commit the seeded values as the pristine baseline (keepValues avoids
      // blanking unmounted fields, which reset() does under shouldUnregister)
      // so formState.dirtyFields reflects only the user's edits on save.
      methods.reset(methods.getValues(), { keepValues: true });
    } else if (isEditMode) {
      if (wizardResourceType === 'customQuery') {
        methods.setValue('testType', 'customQuery');
        methods.setValue('customQueryDestination', 'cloud');
        seedInitialValues();
        methods.reset(methods.getValues(), { keepValues: true });
      } else {
        methods.setValue('testType', 'yamlTest');
        seedInitialValues();
      }
    } else if (wizardResourceType === 'customQuery') {
      methods.setValue('testType', wizardResourceType);
      if (isDataTestsEnabled || !hasDbtProjects) {
        methods.setValue('customQueryDestination', 'cloud');
      }
    } else {
      methods.setValue('resourceType', wizardResourceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardResourceType, wizardMode]);

  useEffect(() => {
    if (!isCloudCustomSql) {
      return;
    }

    seedMissingCustomSqlCloudFields(methods, initialValues);
  }, [isCloudCustomSql, initialValues, methods]);

  const isLastStage = isConfigOnlyFlow
    ? stage === 1
    : wizardResourceType === 'customQuery'
    ? stage === 1
    : stage === 3;

  const isTicketRequired =
    !isPlanMode &&
    requireTicketId &&
    !!activeIntegration &&
    !(CLOUD_TEST_TYPES as readonly string[]).includes(testTypeWatch) &&
    !isCloudCustomSql;

  const selectedResourceIds = methods.watch('resources');
  const selectedAssetsSharedTimestampColumns = useMemo(
    () =>
      getSharedTimestampColumnNames(
        columnsResourcesData,
        selectedResourceIds ?? [],
      ),
    [columnsResourcesData, selectedResourceIds],
  );

  useEffect(() => {
    if (columnsByAssetId === undefined) {
      return;
    }
    TIMESTAMP_FORM_FIELDS.forEach((field) => {
      const value = methods.getValues(field);
      if (value && !selectedAssetsSharedTimestampColumns.includes(value)) {
        methods.setValue(field, '', { shouldValidate: true });
      }
    });
  }, [columnsByAssetId, selectedAssetsSharedTimestampColumns, methods]);

  useWizardUiContext({
    methods,
    stage,
    wizardStages,
    wizardMode,
    wizardResourceType,
    editTestId,
    editTestMetadata,
    selectableTests,
    selectableResourceIds,
    isConfigOnlyFlow,
    isTestConfigEdited,
    isTicketRequired,
    timestampColumns: selectedAssetsSharedTimestampColumns,
  });

  const injectNameIntoYaml = useCallback(() => {
    const testNameOverride = methods.watch('testNameOverride');
    const currentYaml = methods.watch('yaml');

    if (!testNameOverride?.trim() || !currentYaml) return;

    // Parse the YAML, add the name property, and re-serialize
    try {
      const lines = currentYaml.split('\n');
      // Find the test name line (first non-comment, non-empty line with a key)
      const testLineIndex = lines.findIndex(
        (line) =>
          line.trim() && !line.trim().startsWith('#') && line.includes(':'),
      );

      if (testLineIndex === -1) return;

      // Find the indentation level of the test properties
      const nextContentLine = lines
        .slice(testLineIndex + 1)
        .find((line) => line.trim() && !line.trim().startsWith('#'));
      const indent = nextContentLine
        ? nextContentLine.match(/^(\s*)/)?.[1] || '  '
        : '  ';

      // Check if name already exists in the yaml
      const nameLineIndex = lines.findIndex(
        (line) =>
          line.trim().startsWith('name:') && !line.trim().startsWith('#'),
      );

      if (nameLineIndex !== -1) {
        // Update existing name line
        lines[nameLineIndex] = `${indent}name: '${testNameOverride.trim()}'`;
      } else {
        // Insert name as the last property before any trailing comments or empty lines
        // Find the last non-empty, non-comment line
        let insertIndex = lines.length;
        for (let i = lines.length - 1; i > testLineIndex; i--) {
          const line = lines[i].trim();
          if (line && !line.startsWith('#')) {
            insertIndex = i + 1;
            break;
          }
        }
        lines.splice(
          insertIndex,
          0,
          `${indent}name: '${testNameOverride.trim()}'`,
        );
      }

      methods.setValue('yaml', lines.join('\n'));
    } catch {
      // If parsing fails, don't modify the yaml
    }
  }, [methods]);

  const handleNextClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (isLastStage) {
      return;
    }

    e.preventDefault();
    setKeepConfigStageMounted(true);
    setStage((prev) => prev + 1);
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (isLastStage && isCloudCustomSql) {
      e.preventDefault();
      if (!applyCustomSqlCloudSubmitErrors(methods)) {
        return;
      }
      void onSubmit(methods.getValues());
      return;
    }
    methods.handleSubmit(onSubmit)(e);
  };

  const handleHeaderBack = () => {
    if (stage > 1) {
      setStage((prev) => prev - 1);
      return;
    }
    requestExit();
  };

  const selectedResourcesCount = methods.watch('resources')?.length ?? 0;
  const selectedResourceType = methods.watch('resourceType');
  const selectedResourcesLabel =
    selectedResourcesCount > 0 &&
    (selectedResourceType === 'tables' || selectedResourceType === 'columns')
      ? `${pluralizeString(
          selectedResourcesCount,
          selectedResourceType === 'columns' ? 'column' : 'table',
          selectedResourceType === 'columns' ? 'columns' : 'tables',
        )} selected`
      : 'Choose the assets you want to test';

  const isPrimaryActionDisabled =
    isCustomQueryPrBlocked ||
    (!isCloudCustomSql &&
      !isStageValid(
        methods,
        stage,
        isConfigOnlyFlow,
        isTestConfigEdited,
        isTicketRequired,
      ));

  const isPrimaryActionLoading =
    isAddTestsLoading ||
    isAddSingularTestLoading ||
    isAddYamlTestLoading ||
    isEditGenericTestLoading ||
    isAddCloudTestLoading ||
    isAddCustomSqlCloudTestLoading ||
    isUpdateCustomSqlConfigLoading ||
    isCheckDuplicatesLoading ||
    isCheckCreatableLoading;

  const primaryActionLabel = useMemo(() => {
    if (!isLastStage) return 'Next';
    if (isPlanMode) return 'Save to plan';
    if (isCloudCustomSql) return isEditMode ? 'Save' : 'Create';
    return (CLOUD_TEST_TYPES as readonly string[]).includes(testTypeWatch)
      ? 'Submit'
      : 'Submit & Open PR';
  }, [isLastStage, isPlanMode, isCloudCustomSql, isEditMode, testTypeWatch]);

  const stageValidationError = isPrimaryActionDisabled
    ? getStageValidationError(
        methods,
        stage,
        isConfigOnlyFlow,
        selectedAssetsSharedTimestampColumns,
      )
    : undefined;
  const headerMessage = notCreatableReason || stageValidationError;

  const breadcrumbs = [
    {
      label: 'Test Configuration',
      onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        requestExit();
      },
    },
    isPlanMode ? 'Edit plan test' : isEditMode ? 'Edit test' : 'Add test',
  ];

  return (
    <PageWrapper>
      <Form onSubmit={handleFormSubmit}>
        <PageHeader>
          <HeaderNavigation>
            <HeaderNavigationRow>
              <BackButton
                onClick={handleHeaderBack}
                aria-label="Back"
                size="small"
                sx={{ p: '4px' }}
              >
                <SvgIcon width={16} height={14} fillColor="icon.faint">
                  <ArrowLeft />
                </SvgIcon>
              </BackButton>
              <Breadcrumbs items={breadcrumbs} showTreeButton={false} />
              {wizardResourceType === 'customQuery' &&
                !isEditMode &&
                isDataTestsEnabled &&
                hasDbtProjects && (
                  <CustomQueryDestinationSelector
                    methods={methods}
                    isPrDestinationDisabled={shouldConnectCodeRepo}
                  />
                )}
              {!!headerMessage && (
                <OverflowTooltip
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    justifyContent: 'flex-end',
                    fontSize: fontSize('xs'),
                  }}
                  typographySx={{
                    minWidth: 0,
                    color: notCreatableReason
                      ? 'status.error.bright'
                      : 'content.tertiary',
                  }}
                >
                  {headerMessage}
                </OverflowTooltip>
              )}
            </HeaderNavigationRow>
            <SelectedCountLabel>{selectedResourcesLabel}</SelectedCountLabel>
          </HeaderNavigation>
          <HeaderActions>
            {activeIntegration &&
              isLastStage &&
              !(CLOUD_TEST_TYPES as readonly string[]).includes(
                testTypeWatch,
              ) &&
              !isCloudCustomSql && (
                <>
                  <Controller
                    name="ticketId"
                    control={methods.control}
                    shouldUnregister={false}
                    render={({ field }) => (
                      <ConnectTicketDropdown
                        envId={selectedEnvId!}
                        enabledIntegrations={enabledIntegrations}
                        onSelect={(integration, ticket) => {
                          handleIntegrationSelect(integration.id);
                          field.onChange(ticket.key);
                        }}
                        onCreateClick={(integration) =>
                          handleCreateTicketClick(integration.id)
                        }
                        selectedTicket={
                          field.value
                            ? {
                                key: field.value,
                                integrationType: activeIntegration.type,
                              }
                            : null
                        }
                        onClearSelected={() => field.onChange(null)}
                        popoverPlacement="below"
                      />
                    )}
                  />
                  <CreateTicketDialogs
                    open={createTicketDialogOpen}
                    onClose={handleCreateTicketClose}
                    onTicketCreated={handleTicketCreated}
                    activeIntegration={activeIntegration}
                    envId={selectedEnvId!}
                  />
                </>
              )}
            {wizardResourceType === 'customQuery' &&
              account?.ai_features_enabled && (
                <GenerateCustomSqlPopupButton
                  onGenerated={handleCustomQueryGenerated}
                />
              )}
            <Flex sx={{ alignItems: 'center', gap: 2 }}>
              <Tooltip
                title={
                  isPrimaryActionDisabled
                    ? isCustomQueryPrBlocked
                      ? 'Connect your code repository to open a PR'
                      : stageValidationError ?? currentWizardStage.hint
                    : ''
                }
              >
                <Flex sx={{ flexShrink: 0 }}>
                  <LoadingButton
                    size="medium"
                    disabled={isPrimaryActionDisabled}
                    type={isLastStage ? 'submit' : 'button'}
                    loading={isPrimaryActionLoading}
                    onClick={handleNextClick}
                    endIcon={
                      !isLastStage ? (
                        <ButtonIconWrapper $disabled={isPrimaryActionDisabled}>
                          <ArrowRight />
                        </ButtonIconWrapper>
                      ) : undefined
                    }
                  >
                    {primaryActionLabel}
                  </LoadingButton>
                </Flex>
              </Tooltip>
            </Flex>
          </HeaderActions>
        </PageHeader>

        {showStepper && !isPlanMode && (
          <TestDialogStepper
            mode={wizardResourceType}
            stage={stage}
            watch={methods.watch}
            onStageClick={(index) => setStage(index)}
            tableResourcesData={tableResourcesData}
            columnsResourcesData={columnsResourcesData}
            editMode={isEditMode}
          />
        )}

        {isPlanMode ? (
          <PlanEditTestWizardStages
            methods={methods}
            stage={stage}
            isCustomQuery={planSource === 'custom-sql-plan'}
            singularTestPaths={singularTestPaths}
            tableResourcesData={tableResourcesData}
            isDataTestsEnabled={isDataTestsEnabled}
          />
        ) : isEditMode && wizardResourceType === 'customQuery' ? (
          <AddCustomQueryTestWizardStages
            methods={methods}
            singularTestPaths={singularTestPaths}
            tableResourcesData={tableResourcesData}
            isDataTestsEnabled={isDataTestsEnabled}
            editMode
          />
        ) : isEditMode ? (
          <EditTestWizardStages
            methods={methods}
            stage={stage}
            columnsResourcesData={columnsResourcesData}
          />
        ) : wizardResourceType === 'customQuery' ? (
          <AddCustomQueryTestWizardStages
            methods={methods}
            singularTestPaths={singularTestPaths}
            tableResourcesData={tableResourcesData}
            isDataTestsEnabled={isDataTestsEnabled}
          />
        ) : (
          <AddTestWizardStages
            methods={methods}
            stage={stage}
            tableResourcesData={tableResourcesData}
            columnsResourcesData={columnsResourcesData}
            keepConfigStageMounted={keepConfigStageMounted}
            isResourcesLoading={
              isResourcesLoading ||
              (selectedResourceType === 'columns' &&
                (isColumnsLoading || isColumnsError))
            }
            warehouseType={warehouseType}
          />
        )}
      </Form>

      <Dialog open={showCloseConfirmDialog}>
        <FlexColumn sx={{ width: '416px' }}>
          <FlexColumn sx={{ gap: 2, p: 3 }}>
            <Typography sx={{ fontWeight: fontWeight('semibold') }}>
              Unsaved changes
            </Typography>
            <Typography>Your changes will be lost if you exit.</Typography>
          </FlexColumn>
          <Divider />
          <Flex sx={{ gap: 1, p: 3, justifyContent: 'flex-end' }}>
            <Button
              color="secondary"
              size="large"
              onClick={() => setShowCloseConfirmDialog(false)}
            >
              Keep editing
            </Button>
            <Button size="large" onClick={handleClose}>
              Discard changes
            </Button>
          </Flex>
        </FlexColumn>
      </Dialog>

      <OverrideTestsConfirmationDialog
        open={showOverrideDialog}
        onClose={handleOverrideCancel}
        onConfirm={handleOverrideConfirm}
        duplicates={duplicates}
        isLoading={isAddCloudTestLoading}
        totalSubmittedTestsCount={pendingCloudTestArgs?.length || 0}
      />
    </PageWrapper>
  );
};

export default AddTestWizardPage;
